import { useContext } from 'react'
import React from 'react'
import {
  CommandBarButton,
  ContextualMenu,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  DirectionalHint,
  IButtonStyles,
  IContextualMenuItem,
  IStackStyles,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  StackItem,
  Text,
  TooltipHost
} from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'

import { ChatHistoryLoadingState, historyDeleteAll } from '../../api'

import ChatHistoryList from './ChatHistoryList'

import styles from './ChatHistoryPanel.module.css'
import { CloseIcon } from '../../assets'
import { AppStateContext } from '../../state/AppProvider'

interface ChatHistoryPanelProps { }

export enum ChatHistoryPanelTabs {
  History = 'History'
}

const commandBarButtonStyle: IButtonStyles = {
  root: {
    padding: '0',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    borderRadius: '8px',
    minWidth: '36px',
    width: '36px',
    height: '36px'
  },
  icon: { color: '#000' },
  iconHovered: { color: '#000' }
}

const commandBarStyle: Partial<IStackStyles> = { root: { height: '42px' } }

const calloutProps = {
  styles: {
    root: { borderRadius: '4px' },
    beak: { borderRadius: '2px' },
    beakCurtain: { borderRadius: '4px' },
  }
}

export function ChatHistoryPanel(_props: ChatHistoryPanelProps) {
  const appStateContext = useContext(AppStateContext)
  const [showContextualMenu, setShowContextualMenu] = React.useState(false)
  const [hideClearAllDialog, { toggle: toggleClearAllDialog }] = useBoolean(true)
  const [clearing, setClearing] = React.useState(false)
  const [clearingError, setClearingError] = React.useState(false)

  const clearAllDialogContentProps = {
    type: DialogType.close,
    title: !clearingError ? 'Are you sure you want to clear all chat history?' : 'Error deleting all of chat history',
    closeButtonAriaLabel: 'Close',
    subText: !clearingError
      ? 'All chat history will be permanently removed.'
      : 'Please try again. If the problem persists, please contact the site administrator.'
  }

  const modalProps = {
    titleAriaId: 'labelId',
    subtitleAriaId: 'subTextId',
    isBlocking: true,
    styles: { main: { maxWidth: 450 } }
  }

  const menuItems: IContextualMenuItem[] = [
    {
      key: 'clearAll',
      text: 'Clear all',
      itemProps: {
        styles: {
          item: {
            minWidth: 'auto',
          },
          root: {
            selectors: {
              ':hover, :active': {
                color: "#FFF",
                backgroundColor: '#0F6CBD',
              }
            },
          },
        }
      }
    }
  ]

  const handleHistoryClose = () => {
    appStateContext?.dispatch({ type: 'TOGGLE_CHAT_HISTORY' })
  }

  const onShowContextualMenu = React.useCallback((ev: React.MouseEvent<HTMLElement>) => {
    ev.preventDefault() // don't navigate
    setShowContextualMenu(true)
  }, [])

  const onHideContextualMenu = React.useCallback(() => setShowContextualMenu(false), [])

  const onClearAllChatHistory = async () => {
    setClearing(true)
    const response = await historyDeleteAll()
    if (!response.ok) {
      setClearingError(true)
    } else {
      appStateContext?.dispatch({ type: 'DELETE_CHAT_HISTORY' })
      toggleClearAllDialog()
    }
    setClearing(false)
  }

  const onHideClearAllDialog = () => {
    toggleClearAllDialog()
    setTimeout(() => {
      setClearingError(false)
    }, 2000)
  }

  React.useEffect(() => { }, [appStateContext?.state.chatHistory, clearingError])

  return (
    <section className={styles.container} data-is-scrollable aria-label={'chat history panel'}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center" wrap aria-label="chat history header" style={{ paddingRight: '8px' }}>
        <StackItem>
          <Text
            role="heading"
            aria-level={2}
            style={{
              fontFamily: 'Arial',
              fontSize: '20px',
              fontWeight: '400',
              lineHeight: '24px'
            }}>
            Chat history
          </Text>
        </StackItem>
        <Stack verticalAlign="start">
          <Stack horizontal styles={commandBarStyle} tokens={{ childrenGap: 4 }}>
            <TooltipHost
              content={'Clear All'}
              id="clearHistory"
              directionalHint={DirectionalHint.leftCenter}
              calloutProps={calloutProps}>
              <CommandBarButton
                iconProps={{ iconName: 'More' }}
                onClick={onShowContextualMenu}
                aria-describedby='clearHistory'
                aria-label={'clear all chat history'}
                styles={commandBarButtonStyle}
                role="button"
                id="moreButton"
              />
            </TooltipHost>
            <ContextualMenu
              items={menuItems}
              hidden={!showContextualMenu}
              target={'#moreButton'}
              onItemClick={toggleClearAllDialog}
              onDismiss={onHideContextualMenu}
              styles={{ root: { minWidth: '50px' } }}
              calloutProps={{ styles: { root: { borderRadius: '8px', overflow: 'hidden' } } }}
            />
            <TooltipHost
              content={'Close history panel'}
              id="closeHistory"
              directionalHint={DirectionalHint.rightCenter}>
              <CommandBarButton
                title={'Hide'}
                onClick={handleHistoryClose}
                aria-describedby='closeHistory'
                aria-label={'close button'}
                styles={commandBarButtonStyle}
                role="button"
              >
                <img src={CloseIcon} alt="" />
              </CommandBarButton>
            </TooltipHost>
          </Stack>
        </Stack>
      </Stack>
      <Stack aria-label="chat history panel content" className={styles.panelContent}>
        <Stack styles={{ root: { maxWidth: '100%', overflow: 'hidden' } }}>
          {appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Success &&
            appStateContext?.state.isCosmosDBAvailable.cosmosDB && <ChatHistoryList />}
          {appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail &&
            appStateContext?.state.isCosmosDBAvailable && (
              <>
                <Stack>
                  <Stack horizontalAlign="center" verticalAlign="center" style={{ width: '100%', marginTop: 10 }}>
                    <StackItem>
                      <Text style={{ alignSelf: 'center', fontWeight: '400', fontSize: 16 }}>
                        {appStateContext?.state.isCosmosDBAvailable?.status && (
                          <span>{appStateContext?.state.isCosmosDBAvailable?.status}</span>
                        )}
                        {!appStateContext?.state.isCosmosDBAvailable?.status && <span>Error loading chat history</span>}
                      </Text>
                    </StackItem>
                    <StackItem>
                      <Text style={{ alignSelf: 'center', fontWeight: '400', fontSize: 14 }}>
                        <span>Chat history can't be saved at this time</span>
                      </Text>
                    </StackItem>
                  </Stack>
                </Stack>
              </>
            )}
          {appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading && (
            <>
              <Stack>
                <Stack
                  horizontal
                  horizontalAlign="center"
                  verticalAlign="center"
                  style={{ width: '100%', marginTop: 10 }}>
                  <StackItem style={{ justifyContent: 'center', alignItems: 'center' }}>
                    <Spinner
                      style={{ alignSelf: 'flex-start', height: '100%', marginRight: '5px' }}
                      size={SpinnerSize.medium}
                    />
                  </StackItem>
                  <StackItem>
                    <Text style={{ alignSelf: 'center', fontWeight: '400', fontSize: 14 }}>
                      <span style={{ whiteSpace: 'pre-wrap' }}>Loading chat history</span>
                    </Text>
                  </StackItem>
                </Stack>
              </Stack>
            </>
          )}
        </Stack>
      </Stack>
      <Dialog
        hidden={hideClearAllDialog}
        onDismiss={clearing ? () => { } : onHideClearAllDialog}
        dialogContentProps={clearAllDialogContentProps}
        modalProps={modalProps}>
        <DialogFooter>
          {!clearingError && <PrimaryButton onClick={onClearAllChatHistory} disabled={clearing} text="Clear All" />}
          <DefaultButton
            onClick={onHideClearAllDialog}
            disabled={clearing}
            text={!clearingError ? 'Cancel' : 'Close'}
          />
        </DialogFooter>
      </Dialog>
    </section>
  )
}
